import React, { useCallback, useRef } from 'react'
import { UserService } from '@duck/api-definitions'
import { AsyncMultiPick, type AsyncMultiPickProps, type MultiPickOption } from '@duck/ui'
import { apis } from '@/modules/apiClients'

export interface UserDataInputProps extends Omit<AsyncMultiPickProps, 'mode' | 'fetchOptions'> {
  /** Max number of results to return from the backend. Default: 20. */
  maxResults?: number
  /** Called with the full UserSearchResult objects for the currently selected values. */
  onChangeFullObjects?: (users: UserService.UserSearchResult[]) => void
}

/**
 * Shared user search -> options mapper, reused by both the generic data input
 * and table filters that need async user search.
 */
export const fetchUserOptions = async (
  query?: string,
  maxResults = 20,
): Promise<MultiPickOption[]> => {
  const trimmed = query?.trim()
  if (!trimmed) return []

  const { data } = await apis.userService.searchGet(trimmed, maxResults)

  return data.map((u: UserService.UserSearchResult) => ({
    value: u.id,
    label: `${u.firstName} ${u.lastName} (${u.email})`,
  }))
}

export const UserDataInput: React.FC<UserDataInputProps> = ({
  maxResults = 20,
  onChangeFullObjects,
  onChange,
  ...props
}) => {
  // Keep a map of id -> UserSearchResult from the latest fetch
  const userMapRef = useRef<Map<string, UserService.UserSearchResult>>(new Map())

  const fetchOptions = useCallback(
    async (query?: string): Promise<MultiPickOption[]> => {
      const trimmed = query?.trim()
      if (!trimmed) return []

      const { data } = await apis.userService.searchGet(trimmed, maxResults)

      for (const u of data) {
        userMapRef.current.set(u.id, u)
      }

      return data.map((u: UserService.UserSearchResult) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName} (${u.email})`,
      }))
    },
    [maxResults],
  )

  const handleChange = useCallback(
    (selectedIds: string[]) => {
      onChange?.(selectedIds)
      if (onChangeFullObjects) {
        const users = selectedIds
          .map(id => userMapRef.current.get(id))
          .filter((u): u is UserService.UserSearchResult => !!u)
        onChangeFullObjects(users)
      }
    },
    [onChange, onChangeFullObjects],
  )

  return (
    <AsyncMultiPick
      mode="search"
      fetchOptions={fetchOptions}
      onChange={handleChange}
      {...props}
    />
  )
}

